import { useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type PieLabelRenderProps,
} from 'recharts';

import { IconClose, IconInfo, IconPrinter, IconRefreshCw, IconSearch } from '../../components/layout/icons';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ExcelExportButton } from '../../components/ui/ExcelExportButton';
import { Modal } from '../../components/ui/Modal';
import { ApiError } from '../../lib/api-client';
import { exportRowsToExcel } from '../../lib/excel-export';
import { formatIpdDateTime } from '../ipd-daycare/ipd-daycare.utils';
import { currentMonthDateFilters, emptyDateFilters, todayIso, type DateFilters } from './report-filters';
import { ReportAnalyticsGrid, ReportBarChartCard, type ReportChartDatum } from './ReportAnalytics';
import { useCollectionDetails, useCollectionReport } from './reports.queries';
import type { CollectionDateRow, CollectionDetailFilters, CollectionUserRow, ReportSection } from './types';

type CollectionTab = 'date' | 'user';

interface CollectionReportPageProps {
  defaultTab?: CollectionTab;
}

const departmentGroups: { key: ReportSection; label: string; chartColor: string; cashColor: string; bankColor: string }[] = [
  { key: 'opd', label: 'OPD', chartColor: '#87c3ef', cashColor: '#87c3ef', bankColor: '#ffd267' },
  { key: 'emg', label: 'EMG', chartColor: '#9fd39f', cashColor: '#9fd39f', bankColor: '#ffd05c' },
  { key: 'ipd', label: 'IPD', chartColor: '#ad9bd5', cashColor: '#a996d3', bankColor: '#ffc7b8' },
  { key: 'daycare', label: 'Daycare', chartColor: '#78c8c3', cashColor: '#76c7c1', bankColor: '#f5b5cc' },
  { key: 'dialysis', label: 'Dialysis', chartColor: '#69bdb3', cashColor: '#72c9c0', bankColor: '#f3aec7' },
  { key: 'ot', label: 'OT', chartColor: '#e5a46b', cashColor: '#e8a970', bankColor: '#9cc3e6' },
  { key: 'investigation', label: 'Investigation', chartColor: '#c682d1', cashColor: '#c57ed0', bankColor: '#b69f94' },
];
const chartDepartmentGroups = departmentGroups.filter((group) => group.key !== 'ot');

const formatAmount = (value: number) => value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
const formatChartAmount = (value: number) => value.toLocaleString('en-IN', { notation: 'compact', maximumFractionDigits: 1 });
const formatReportDate = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : value;
};

const dateFiltersFromSearch = (searchParams: URLSearchParams): DateFilters => {
  if (!searchParams.has('fromDate') && !searchParams.has('toDate')) return currentMonthDateFilters();
  return {
    fromDate: searchParams.get('fromDate') ?? '',
    toDate: searchParams.get('toDate') ?? '',
  };
};

const renderPaymentLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }: PieLabelRenderProps) => {
  const share = Number(percent ?? 0);
  if (share < 0.045) return null;
  const radius = Number(innerRadius ?? 0) + (Number(outerRadius ?? 0) - Number(innerRadius ?? 0)) * 0.58;
  const angle = -Number(midAngle ?? 0) * (Math.PI / 180);
  const x = Number(cx) + radius * Math.cos(angle);
  const y = Number(cy) + radius * Math.sin(angle);
  return (
    <text x={x} y={y} fill="#111827" textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight={700}>
      <tspan x={x} dy="-0.35em">{String(name ?? '')}</tspan>
      <tspan x={x} dy="1.1em">{`${(share * 100).toFixed(1)}%`}</tspan>
    </text>
  );
};

const PdfIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 2h8l4 4v16H6z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <path d="M14 2v5h5M8 16h8M8 12h5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export const CollectionReportPage = ({ defaultTab = 'date' }: CollectionReportPageProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<CollectionTab>(() => {
    const requestedView = searchParams.get('view');
    return requestedView === 'date' || requestedView === 'user' ? requestedView : defaultTab;
  });
  const [draft, setDraft] = useState<DateFilters>(() => dateFiltersFromSearch(searchParams));
  const [filters, setFilters] = useState<DateFilters>(() => dateFiltersFromSearch(searchParams));
  const query = useCollectionReport(filters);

  const persistReportState = (nextFilters: DateFilters, nextTab: CollectionTab) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('view', nextTab);
    nextParams.set('fromDate', nextFilters.fromDate);
    nextParams.set('toDate', nextFilters.toDate);
    setSearchParams(nextParams, { replace: true });
  };
  const changeTab = (nextTab: CollectionTab) => {
    setTab(nextTab);
    persistReportState(filters, nextTab);
  };
  const reset = () => {
    const next = emptyDateFilters();
    setDraft(next);
    setFilters(next);
    persistReportState(next, tab);
  };
  const periodLabel = filters.fromDate || filters.toDate
    ? `${filters.fromDate || 'earliest'} to ${filters.toDate || 'latest'}`
    : 'all dates';
  const errorMessage = query.error instanceof ApiError ? query.error.message : 'Collection report could not be loaded.';
  const sectionChartData = useMemo<ReportChartDatum[]>(() => chartDepartmentGroups.map((group, index) => {
    const totals = (query.data?.dateRows ?? []).reduce((summary, row) => {
      summary.Cash += row.sections[group.key].cash;
      summary.Bank += row.sections[group.key].bank;
      summary.Total += row.sections[group.key].total;
      return summary;
    }, { Cash: 0, Bank: 0, Total: 0 });
    return { name: group.label.toUpperCase(), ...totals, color: group.chartColor, sectionIndex: index };
  }), [query.data?.dateRows]);
  const userChartData = useMemo<ReportChartDatum[]>(() => [...(query.data?.userRows ?? [])]
    .sort((left, right) => right.totals.total - left.totals.total)
    .slice(0, 8)
    .map((row) => ({
      name: row.userName,
      Cash: row.totals.cash,
      Bank: row.totals.bank,
      Refund: row.totals.refund,
      Total: row.totals.total,
    })), [query.data?.userRows]);

  const exportToExcel = async () => {
    if (!query.data) return;
    if (tab === 'date') {
      await exportRowsToExcel({
        fileName: `Collection-Date-Wise-${new Date().toISOString().slice(0, 10)}`,
        title: 'Date wise Collection',
        subtitle: periodLabel,
        columns: [
          { header: 'Date', width: 14, map: (row: CollectionDateRow) => row.date },
          ...departmentGroups.flatMap((group) => [
            { header: `${group.label} Cash`, width: 14, align: 'right' as const, numFmt: '#,##0.00', map: (row: CollectionDateRow) => row.sections[group.key].cash },
            { header: `${group.label} Bank`, width: 14, align: 'right' as const, numFmt: '#,##0.00', map: (row: CollectionDateRow) => row.sections[group.key].bank },
            { header: `${group.label} Total`, width: 14, align: 'right' as const, numFmt: '#,##0.00', map: (row: CollectionDateRow) => row.sections[group.key].total },
          ]),
          { header: 'Cash', width: 14, align: 'right', numFmt: '#,##0.00', map: (row: CollectionDateRow) => row.totals.cash },
          { header: 'Refund', width: 14, align: 'right', numFmt: '#,##0.00', map: (row: CollectionDateRow) => row.totals.refund },
          { header: 'Bank', width: 14, align: 'right', numFmt: '#,##0.00', map: (row: CollectionDateRow) => row.totals.bank },
          { header: 'TDS', width: 14, align: 'right', numFmt: '#,##0.00', map: (row: CollectionDateRow) => row.totals.tds },
          { header: 'Total', width: 14, align: 'right', numFmt: '#,##0.00', map: (row: CollectionDateRow) => row.totals.total },
        ],
        rows: query.data.dateRows,
      });
      return;
    }
    await exportRowsToExcel({
      fileName: `Collection-User-Wise-${new Date().toISOString().slice(0, 10)}`,
      title: 'User wise Collection',
      subtitle: periodLabel,
      columns: [
        { header: 'User', width: 24, map: (row: CollectionUserRow) => row.userName },
        { header: 'Branch', width: 12, map: (row: CollectionUserRow) => row.branch },
        ...departmentGroups.map((group) => ({ header: group.label, width: 14, align: 'right' as const, numFmt: '#,##0.00', map: (row: CollectionUserRow) => row.sections[group.key] })),
        { header: 'Cash', width: 14, align: 'right', numFmt: '#,##0.00', map: (row: CollectionUserRow) => row.totals.cash },
        { header: 'Bank', width: 14, align: 'right', numFmt: '#,##0.00', map: (row: CollectionUserRow) => row.totals.bank },
        { header: 'Refund', width: 14, align: 'right', numFmt: '#,##0.00', map: (row: CollectionUserRow) => row.totals.refund },
        { header: 'TDS', width: 14, align: 'right', numFmt: '#,##0.00', map: (row: CollectionUserRow) => row.totals.tds },
        { header: 'Total', width: 14, align: 'right', numFmt: '#,##0.00', map: (row: CollectionUserRow) => row.totals.total },
      ],
      rows: query.data.userRows,
    });
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setFilters(draft);
    persistReportState(draft, tab);
  };

  return (
    <div className="page-stack collection-reference-page">
      <section className="content-card collection-report-card collection-reference-report">
        <header className="general-settings-titlebar collection-report-titlebar">
          <h2>Collection Report</h2>
          <div className="collection-view-tabs report-print-hidden" role="tablist" aria-label="Collection report views">
            <button type="button" role="tab" aria-selected={tab === 'date'} className={tab === 'date' ? 'active' : ''} onClick={() => changeTab('date')}>Date Wise Collection</button>
            <button type="button" role="tab" aria-selected={tab === 'user'} className={tab === 'user' ? 'active' : ''} onClick={() => changeTab('user')}>Users Wise Collection</button>
          </div>
        </header>

        <form className="collection-reference-toolbar report-print-hidden" onSubmit={submitSearch}>
          <div className="collection-reference-dates">
            <label>
              <span className="sr-only">From date</span>
              <input
                aria-label="From date"
                type="date"
                value={draft.fromDate}
                max={draft.toDate || todayIso()}
                onChange={(event) => setDraft({ ...draft, fromDate: event.target.value })}
              />
            </label>
            <label>
              <span className="sr-only">To date</span>
              <input
                aria-label="To date"
                type="date"
                value={draft.toDate}
                min={draft.fromDate || undefined}
                max={todayIso()}
                onChange={(event) => setDraft({ ...draft, toDate: event.target.value })}
              />
            </label>
          </div>
          <div className="collection-reference-actions">
            <button className="collection-reference-button search" type="submit"><IconSearch />Search</button>
            <button className="collection-reference-button reset" type="button" onClick={reset}><IconRefreshCw />Reset</button>
            <button className="collection-reference-button print" type="button" onClick={() => window.print()}><IconPrinter />Print</button>
            <ExcelExportButton className="collection-reference-excel" onExport={exportToExcel} disabled={!query.isSuccess} label="Excel" />
            <button className="collection-reference-button pdf" type="button" title="Open the print dialog to save as PDF" onClick={() => window.print()}><PdfIcon />PDF</button>
          </div>
        </form>

        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? <ErrorState title="Unable to load collection report" message={errorMessage} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
        {query.isSuccess && tab === 'date' ? (
          <CollectionCharts data={sectionChartData} />
        ) : null}
        {query.isSuccess && tab === 'user' ? (
          <ReportAnalyticsGrid>
            <ReportBarChartCard
              eyebrow="User analysis"
              title="Top user collections"
              data={userChartData}
              bars={[{ key: 'Total', name: 'Total', color: '#0d6b66' }]}
              layout="vertical"
              height={Math.max(240, userChartData.length * 34 + 90)}
              valueKind="amount"
            />
            <ReportBarChartCard
              eyebrow="Payment split"
              title="Cash and bank by user"
              data={userChartData}
              bars={[
                { key: 'Cash', name: 'Cash', color: '#28a765', stackId: 'payment' },
                { key: 'Bank', name: 'Bank', color: '#3777b7', stackId: 'payment' },
                { key: 'Refund', name: 'Refund', color: '#b1424d' },
              ]}
              layout="vertical"
              height={Math.max(240, userChartData.length * 34 + 90)}
              valueKind="amount"
            />
          </ReportAnalyticsGrid>
        ) : null}
        {query.isSuccess && tab === 'date' ? <DateCollectionTable rows={query.data.dateRows} /> : null}
        {query.isSuccess && tab === 'user' ? <UserCollectionTable rows={query.data.userRows} filters={filters} /> : null}
      </section>
    </div>
  );
};

const CollectionCharts = ({ data }: { data: ReportChartDatum[] }) => {
  const hasChartValues = data.some((entry) => Number(entry.Total ?? 0) !== 0);
  return (
    <div className="collection-overview-grid">
      <section className="collection-total-chart-card" aria-label="Total collection by department">
        {hasChartValues ? (
          <>
            <ResponsiveContainer width="100%" height={290}>
              <BarChart data={data} margin={{ top: 26, right: 14, left: 0, bottom: 8 }} barCategoryGap="34%">
                <CartesianGrid vertical={false} stroke="#e7eaec" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#17222d', fontWeight: 700 }} axisLine={{ stroke: '#dde2e5' }} tickLine={false} interval={0} angle={-24} textAnchor="end" height={48} />
                <YAxis tick={{ fontSize: 10, fill: '#17222d', fontWeight: 600 }} axisLine={false} tickLine={false} width={50} tickFormatter={(value: number) => formatChartAmount(value)} />
                <Tooltip formatter={(value) => formatAmount(Number(value ?? 0))} />
                <Bar dataKey="Total" radius={[5, 5, 0, 0]} maxBarSize={38}>
                  {data.map((entry, index) => <Cell key={String(entry.name)} fill={String(entry.color ?? chartDepartmentGroups[index]?.chartColor)} />)}
                  <LabelList dataKey="Total" position="top" formatter={(value: unknown) => formatChartAmount(Number(value ?? 0))} style={{ fill: '#26323d', fontSize: 10, fontWeight: 700 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="collection-chart-legend" aria-hidden="true">
              {chartDepartmentGroups.map((group) => <span key={group.key}><i style={{ background: group.chartColor }} />{group.label.toUpperCase()}</span>)}
            </div>
          </>
        ) : <EmptyState title="No collection data" message="Collection totals will appear here for the selected period." />}
      </section>

      <div className="collection-payment-grid">
        {chartDepartmentGroups.map((group, index) => {
          const entry = data[index];
          const paymentData = [
            { name: 'Cash', value: Number(entry?.Cash ?? 0), color: group.cashColor },
            { name: 'Bank', value: Number(entry?.Bank ?? 0), color: group.bankColor },
          ].filter((item) => item.value !== 0);
          return (
            <section className="collection-payment-card" key={group.key} aria-label={`${group.label} payment split`}>
              <h3>{group.label.toUpperCase()}</h3>
              {paymentData.length > 0 ? (
                <ResponsiveContainer width="100%" height={118}>
                  <PieChart>
                    <Pie data={paymentData} dataKey="value" nameKey="name" outerRadius={49} stroke="#fff" strokeWidth={2} label={renderPaymentLabel} labelLine={false} isAnimationActive={false}>
                      {paymentData.map((item) => <Cell key={item.name} fill={item.color} />)}
                    </Pie>
                    <Tooltip formatter={(value) => formatAmount(Number(value ?? 0))} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <div className="collection-payment-empty">No collection</div>}
            </section>
          );
        })}
      </div>
    </div>
  );
};

const EmptyRow = ({ colSpan }: { colSpan: number }) => (
  <tr>
    <td className="collection-table-empty" colSpan={colSpan}>
      <span className="collection-table-empty-inner">
        <span className="collection-table-empty-icon"><IconInfo width={13} height={13} /></span>
        No collection records found for the selected period
      </span>
    </td>
  </tr>
);

interface CollectionDrilldownSelection extends CollectionDetailFilters {
  label: string;
  periodLabel: string;
}

const collectionBillHref = (row: { module: ReportSection; sourceId: string; admissionId: string | null }): string | null => {
  switch (row.module) {
    case 'opd': return `/opd/${row.sourceId}`;
    case 'emg': return `/emg/${row.sourceId}`;
    case 'investigation': return `/investigation-bill/${row.sourceId}`;
    case 'ipd':
    case 'daycare': return row.admissionId ? `/ipd-daycare/${row.admissionId}?tab=bill-details` : null;
    case 'dialysis': return row.admissionId ? `/dialysis/${row.admissionId}` : null;
    case 'ot': return row.admissionId ? `/ot/${row.admissionId}?tab=bill-details` : null;
  }
  return null;
};

const CollectionTotalButton = ({
  amount,
  active,
  label,
  onClick,
}: {
  amount: number;
  active: boolean;
  label: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    className={`collection-amount-link${active ? ' active' : ''}`}
    aria-expanded={active}
    title={`Show patient payment details for ${label}`}
    onClick={onClick}
  >
    {formatAmount(amount)}
  </button>
);

const CollectionDetailContent = ({ selection, onClose }: { selection: CollectionDrilldownSelection; onClose: () => void }) => {
  const query = useCollectionDetails({
    fromDate: selection.fromDate,
    toDate: selection.toDate,
    section: selection.section,
    userId: selection.userId,
    userName: selection.userName,
  });
  const errorMessage = query.error instanceof ApiError ? query.error.message : 'Collection details could not be loaded.';
  return (
    <div className="collection-detail-panel">
      <header className="collection-detail-heading">
        <div>
          <span>Patient payment details</span>
          <strong id="collection-detail-title">{selection.label} · {selection.periodLabel}</strong>
        </div>
        <button type="button" onClick={onClose} aria-label="Close collection details" title="Close details"><IconClose /></button>
      </header>
      {query.isLoading ? <LoadingState /> : null}
      {query.isError ? <ErrorState title="Unable to load payment details" message={errorMessage} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
      {query.isSuccess && query.data.rows.length === 0 ? <EmptyState title="No payment details found" message="No patient payments are recorded for this total." /> : null}
      {query.isSuccess && query.data.rows.length > 0 ? (
        <div className="collection-detail-table-scroll">
          <table className="collection-detail-table">
            <thead>
              <tr>
                <th>Bill Id</th>
                <th>Patient (UHID)</th>
                <th>Payment Amount</th>
                <th>Payment Date</th>
                <th>Payment Mode</th>
                <th>Account</th>
              </tr>
            </thead>
            <tbody>
              {query.data.rows.map((row) => {
                const billHref = collectionBillHref(row);
                return (
                <tr key={row.id}>
                  <td>
                    {billHref ? (
                      <Link className="collection-detail-bill-id" to={billHref} target="_blank" rel="noreferrer" title="Open bill details in a new tab">{row.billId}</Link>
                    ) : <span className="collection-detail-bill-id">{row.billId}</span>}
                  </td>
                  <td>
                    {row.patientId ? (
                      <Link className="collection-detail-patient" to={`/patients/${row.patientId}`} target="_blank" rel="noreferrer" title="Open patient profile in a new tab">
                        {row.patientName.toUpperCase()}{row.uhid ? ` (${row.uhid})` : ''}
                      </Link>
                    ) : `${row.patientName.toUpperCase()}${row.uhid ? ` (${row.uhid})` : ''}`}
                  </td>
                  <td className={row.isRefund ? 'collection-detail-refund' : ''}>{formatAmount(row.amount)}</td>
                  <td>{formatIpdDateTime(row.date)}</td>
                  <td className={row.isRefund ? 'collection-detail-refund' : ''}>{row.mode}</td>
                  <td>{row.account ?? ''}</td>
                </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={6}>
                  Cash Collection : ₹{formatAmount(query.data.totals.cash)}
                  <span>||</span>
                  Bank Collection : ₹{formatAmount(query.data.totals.bank)}
                  <span>||</span>
                  Total : ₹{formatAmount(query.data.totals.total)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : null}
    </div>
  );
};

const CollectionDetailModal = ({ selection, onClose }: { selection: CollectionDrilldownSelection | null; onClose: () => void }) => (
  <Modal open={selection !== null} onClose={onClose} labelledBy="collection-detail-title" className="collection-detail-modal">
    {selection ? <CollectionDetailContent selection={selection} onClose={onClose} /> : null}
  </Modal>
);

const DateCollectionTable = ({ rows }: { rows: CollectionDateRow[] }) => {
  const columnCount = 1 + departmentGroups.length * 3 + 5;
  const [selection, setSelection] = useState<CollectionDrilldownSelection | null>(null);
  const selectTotal = (date: string, section: ReportSection | null, label: string) => setSelection({
    fromDate: date,
    toDate: date,
    section,
    label,
    periodLabel: formatReportDate(date),
  });
  const isSelected = (date: string, section: ReportSection | null) => selection?.fromDate === date && selection.section === section;
  return (
    <div className="collection-branches">
      <div className="collection-branch-panel">
        <h3 className="collection-branch-title">KGH Collection</h3>
        <div className="collection-table-scroll">
          <table className="collection-table">
            <colgroup>
              <col className="collection-date-column" />
              {departmentGroups.flatMap((group) => [
                <col key={`${group.key}-cash-column`} className="collection-section-column" />,
                <col key={`${group.key}-bank-column`} className="collection-section-column" />,
                <col key={`${group.key}-total-column`} className="collection-section-column" />,
              ])}
              <col className="collection-summary-column" />
              <col className="collection-summary-column" />
              <col className="collection-summary-column" />
              <col className="collection-summary-column" />
              <col className="collection-summary-column" />
            </colgroup>
            <thead>
              <tr>
                <th rowSpan={2}>Date</th>
                {departmentGroups.map((group) => <th key={group.key} colSpan={3}>{group.label}</th>)}
                <th colSpan={5}>Total</th>
              </tr>
              <tr>
                {departmentGroups.flatMap((group) => [
                  <th key={`${group.key}-cash`}>Cash</th>,
                  <th key={`${group.key}-bank`}>Bank</th>,
                  <th key={`${group.key}-total`}>Total</th>,
                ])}
                <th>Cash</th>
                <th>Refund</th>
                <th>Bank</th>
                <th>TDS</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? <EmptyRow colSpan={columnCount} /> : rows.map((row) => (
                <tr key={`${row.branch}-${row.date}`}>
                  <td className="collection-date-cell">{formatReportDate(row.date)}</td>
                  {departmentGroups.flatMap((group) => {
                    const amount = row.sections[group.key];
                    return [
                      <td key={`${group.key}-cash`}>{formatAmount(amount.cash)}</td>,
                      <td key={`${group.key}-bank`}>{formatAmount(amount.bank)}</td>,
                      <td key={`${group.key}-total`}>
                        <CollectionTotalButton
                          amount={amount.total}
                          active={isSelected(row.date, group.key)}
                          label={`${group.label} total on ${row.date}`}
                          onClick={() => selectTotal(row.date, group.key, `${group.label.toUpperCase()} Collection`)}
                        />
                      </td>,
                    ];
                  })}
                  <td>{formatAmount(row.totals.cash)}</td>
                  <td className="collection-alert-value">{formatAmount(row.totals.refund)}</td>
                  <td>{formatAmount(row.totals.bank)}</td>
                  <td className="collection-alert-value">{formatAmount(row.totals.tds)}</td>
                  <td>
                    <CollectionTotalButton
                      amount={row.totals.total}
                      active={isSelected(row.date, null)}
                      label={`overall total on ${row.date}`}
                      onClick={() => selectTotal(row.date, null, 'All Collection')}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <CollectionDetailModal selection={selection} onClose={() => setSelection(null)} />
    </div>
  );
};

const UserCollectionTable = ({ rows, filters }: { rows: CollectionUserRow[]; filters: DateFilters }) => {
  const columnCount = 2 + departmentGroups.length + 5;
  const [selection, setSelection] = useState<CollectionDrilldownSelection | null>(null);
  const selectedUserKey = selection?.userId || selection?.userName;
  const detailPeriodLabel = filters.fromDate || filters.toDate
    ? `${filters.fromDate ? formatReportDate(filters.fromDate) : 'Earliest'} to ${filters.toDate ? formatReportDate(filters.toDate) : 'Latest'}`
    : 'All dates';
  const selectSection = (row: CollectionUserRow, section: ReportSection, sectionLabel: string) => setSelection({
    fromDate: filters.fromDate || undefined,
    toDate: filters.toDate || undefined,
    section,
    userId: row.userId || undefined,
    userName: row.userName,
    label: `${row.userName.toUpperCase()} · ${sectionLabel.toUpperCase()} Collection`,
    periodLabel: detailPeriodLabel,
  });
  if (rows.length === 0) {
    return <EmptyState title="No user collection found" message="Receipts recorded by users will appear here." />;
  }
  return (
    <div className="collection-branches">
      <div className="collection-branch-panel">
        <div className="collection-table-scroll">
          <table className="collection-table user-collection-table">
            <colgroup>
              <col className="user-collection-name-column" />
              <col className="user-collection-branch-column" />
              {departmentGroups.map((group) => <col key={`${group.key}-user-column`} className="user-collection-amount-column" />)}
              <col className="user-collection-amount-column" />
              <col className="user-collection-amount-column" />
              <col className="user-collection-amount-column" />
              <col className="user-collection-amount-column" />
              <col className="user-collection-amount-column" />
            </colgroup>
            <thead>
              <tr>
                <th className="text-left" rowSpan={2}>User</th>
                <th className="text-left" rowSpan={2}>Branch</th>
                {departmentGroups.map((group) => <th key={group.key} rowSpan={2}>{group.label}</th>)}
                <th colSpan={5}>Total</th>
              </tr>
              <tr>
                <th>Cash</th>
                <th>Bank</th>
                <th>Refund</th>
                <th>TDS</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? <EmptyRow colSpan={columnCount} /> : rows.map((row) => (
                <tr key={`${row.branch}-${row.userId || row.userName}`}>
                  <td className="text-left">{row.userName}</td>
                  <td className="text-left">{row.branch}</td>
                  {departmentGroups.map((group) => (
                    <td key={group.key}>
                      <CollectionTotalButton
                        amount={row.sections[group.key]}
                        active={selectedUserKey === (row.userId || row.userName) && selection?.section === group.key}
                        label={`${row.userName} ${group.label} collection`}
                        onClick={() => selectSection(row, group.key, group.label)}
                      />
                    </td>
                  ))}
                  <td>{formatAmount(row.totals.cash)}</td>
                  <td>{formatAmount(row.totals.bank)}</td>
                  <td>{formatAmount(row.totals.refund)}</td>
                  <td>{formatAmount(row.totals.tds)}</td>
                  <td>{formatAmount(row.totals.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <CollectionDetailModal selection={selection} onClose={() => setSelection(null)} />
    </div>
  );
};
